/**
 * PanelShell — shared left-panel chrome for every page.
 *
 * Renders the invariant header (logo + stackform.beta), footer
 * (UserSettings · mode-switcher · ⓘ · optional ✕), collapse toggle,
 * and the About popup portal.  Page-specific content is passed as
 * `children` and fills the space between header and footer.
 *
 * Adding a new page with a left panel?  Wrap its body content in
 * <PanelShell> and the branding, footer and About popup are automatic.
 *
 *   Usage:
 *     <PanelShell theme={theme} toggleTheme={toggleTheme} API_URL={API_URL}>
 *       <MyPageContent />
 *     </PanelShell>
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { RiInformationLine, RiCloseLine } from 'react-icons/ri';
import AboutSection from './AboutSection';
import UserSettingsMenu from './UserSettingsMenu';
import { ModeDropdown } from './Header';
import useContactForm from '../hooks/useContactForm';

// ── Shared constants ────────────────────────────────────────────────────────

/** Horizontal inset used by header, footer, and callers for body content. */
export const PANEL_PADDING_X = '1rem';

const HEADER_PADDING = `1.5rem ${PANEL_PADDING_X} 0.5rem`;
const FOOTER_PADDING = `0.5rem ${PANEL_PADDING_X}`;

const iconBtnBase = {
  background: 'none',
  border: '1px solid var(--border-color, #ccc)',
  cursor: 'pointer',
  padding: '4px 6px',
  borderRadius: '6px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  transition: 'color 0.15s, border-color 0.15s',
};

// ── Component ───────────────────────────────────────────────────────────────

export default function PanelShell({
  children,
  // Collapse (desktop sidebar)
  collapsed = false,
  onToggleCollapse,
  // Mobile close button
  onClose,
  // UserSettings props (all optional — not every page has all settings)
  theme,
  toggleTheme,
  selectedApis,
  setSelectedApis,
  refreshHistories,
  resetMainPanel,
  refreshModelSettings,
  // API URL for the contact form inside the About popup
  API_URL = '',
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isWorkflow = pathname.startsWith('/workflow-builder');

  /* ── About popup ── */
  const { contact, setContact, handleContactSubmit, contactStatus } =
    useContactForm(API_URL);

  const [aboutOpen, setAboutOpen] = useState(false);
  // right is stored as distance from viewport-right so position:fixed can use both
  // left and right, clamping the popup to the panel on both sides.
  const [aboutPos, setAboutPos] = useState({ bottom: 0, left: 0, right: 0 });
  const aboutBtnRef = useRef(null);
  const aboutPopupRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    if (!aboutOpen) return;
    const handler = (e) => {
      if (
        aboutBtnRef.current && !aboutBtnRef.current.contains(e.target) &&
        aboutPopupRef.current && !aboutPopupRef.current.contains(e.target)
      ) setAboutOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [aboutOpen]);

  const handleAboutToggle = () => {
    if (!aboutBtnRef.current) return;
    const btnRect = aboutBtnRef.current.getBoundingClientRect();
    const footerRect = footerRef.current?.getBoundingClientRect();
    const margin = 8;
    const panelLeft  = footerRect ? footerRect.left  : Math.max(margin, btnRect.left - 220);
    const panelRight = footerRect ? footerRect.right : btnRect.right;
    // Anchor both edges to the panel so the popup never overflows left OR right.
    setAboutPos({
      bottom: window.innerHeight - btnRect.top + 6,
      left:  panelLeft  + margin,
      right: window.innerWidth - panelRight + margin,
    });
    setAboutOpen((o) => !o);
  };

  return (
    <>
      {/* ── Collapse / expand toggle (desktop, pokes out to the right) ── */}
      {onToggleCollapse && (
        <button
          className="wf-panel-toggle wf-panel-toggle-left"
          onClick={onToggleCollapse}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={collapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {collapsed ? '›' : '‹'}
        </button>
      )}

      {/* ── Everything below is hidden when collapsed ── */}
      {!collapsed && (
        <>
          {/* ── Header: logo + stackform.beta (· ✕ top-right on mobile overlay) ── */}
          <div style={{
            flexShrink: 0,
            padding: HEADER_PADDING,
            borderBottom: '1px solid var(--panel-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}>
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                cursor: 'pointer', flex: 1, minWidth: 0, overflow: 'hidden',
              }}
              onClick={() => navigate('/chat')}
              title="Go to home"
            >
              <picture>
                <source srcSet="stackformAI.webp" type="image/webp" />
                <img
                  src="stackformAI-sm.png"
                  alt="Stackform Logo"
                  style={{ height: '28px', width: 'auto', flexShrink: 0 }}
                />
              </picture>
              <span style={{
                fontSize: '1.05rem', fontWeight: 'bold',
                color: 'var(--text-color)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                minWidth: 0,
              }}>
                stackform.<span className="gradient-beta">beta</span>
              </span>
            </div>

            {/* ✕ top-right — only in mobile overlay contexts (onClose provided) */}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close panel"
                title="Close"
                style={{ ...iconBtnBase, color: 'var(--text-secondary, #888)', flexShrink: 0 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--color-danger, #dc3545)';
                  e.currentTarget.style.borderColor = 'var(--color-danger, #dc3545)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--text-secondary, #888)';
                  e.currentTarget.style.borderColor = 'var(--border-color, #ccc)';
                }}
              >
                <RiCloseLine size={18} />
              </button>
            )}
          </div>

          {/* ── Body (page-specific content) ── */}
          {children}

          {/* ── Footer: UserSettings · mode-switcher · ⓘ (· ✕ mobile) ── */}
          <div ref={footerRef} style={{
            flexShrink: 0,
            borderTop: '1px solid var(--panel-border)',
            padding: FOOTER_PADDING,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {/* Left: user icon — constrained to panel right edge */}
              <UserSettingsMenu
                theme={theme}
                toggleTheme={toggleTheme}
                selectedApis={selectedApis}
                setSelectedApis={setSelectedApis}
                refreshHistories={refreshHistories}
                resetMainPanel={resetMainPanel}
                refreshModelSettings={refreshModelSettings}
                getPanelRight={() => footerRef.current?.getBoundingClientRect().right}
              />

              {/* Right group: mode-switcher · ⓘ */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <ModeDropdown isWorkflow={isWorkflow} upward />

                <button
                  ref={aboutBtnRef}
                  onClick={handleAboutToggle}
                  title={aboutOpen ? 'Close About Us' : 'About Us & platform info'}
                  style={{
                    ...iconBtnBase,
                    color: aboutOpen
                      ? 'var(--primary-color, #636cff)'
                      : 'var(--text-secondary, #888)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--primary-color, #636cff)';
                    e.currentTarget.style.borderColor = 'var(--primary-color, #636cff)';
                  }}
                  onMouseLeave={(e) => {
                    if (!aboutOpen) e.currentTarget.style.color = 'var(--text-secondary, #888)';
                    e.currentTarget.style.borderColor = 'var(--border-color, #ccc)';
                  }}
                >
                  <RiInformationLine size={18} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── About popup portal ── */}
      {aboutOpen && createPortal(
        <div
          ref={aboutPopupRef}
          style={{
            position: 'fixed',
            bottom: aboutPos.bottom,
            left: aboutPos.left,
            right: aboutPos.right,
            maxHeight: '60vh',
            overflowY: 'auto',
            background: 'var(--panel-background)',
            border: '1px solid var(--panel-border)',
            borderRadius: '10px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            padding: '12px',
            zIndex: 9999,
          }}
        >
          <AboutSection
            contact={contact}
            setContact={setContact}
            handleContactSubmit={handleContactSubmit}
            contactStatus={contactStatus}
          />
        </div>,
        document.body,
      )}
    </>
  );
}
